// apps/spa/src/hooks/useDmTestReminder.ts
// TanStack Query mutation for POST /api/reminders/:id/dm-test.
//
// Sends the current form's embed to the officer's Discord DMs for preview.
// The worker handles createDmChannel → postChannelMessage → writeAuditLog.
//
// Discriminated success/error result:
//   { success: true }
//   { success: false, error: 'dm_closed', message }
//   { success: false, error: 'dispatch_failed', message }
//
// The mutation does NOT call onSuccess automatically (callers pass callbacks
// in mutate({ ... }, { onSuccess, onError }) for flexible toast handling).

import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type DmTestResult =
  | { success: true }
  | { success: false; error: 'dm_closed' | 'dispatch_failed'; message: string };

export type DmTestInput = {
  reminderId?: string; // optional — unsaved drafts may not have an id yet
  embed: object;
};

export function useDmTestReminder() {
  return useMutation({
    mutationFn: ({ reminderId, embed }: DmTestInput) => {
      // Route: use /:id/dm-test when we have an id; fall back to /dm-test for drafts.
      const path = reminderId
        ? `/api/reminders/${reminderId}/dm-test`
        : '/api/reminders/dm-test';
      return api<DmTestResult>(path, {
        method: 'POST',
        body: JSON.stringify({ embed }),
      });
    },
  });
}
